import { v4 as uuidv4 } from 'uuid'
import { ExtendedChatMessage, MessageType } from '../../../../enviroment/store/chatStore'

/**
 * Mock messages for Copa chat widget
 * These can be used for testing and development
 *
 * Usage example:
 * ```tsx
 * import { createUpgradeMessage } from './mockMessages'
 * import { useChatStore } from 'src/enviroment/store/chatStore'
 *
 * const { addTenantMessage } = useChatStore()
 *
 * // Add the mock upgrade message
 * addTenantMessage(createUpgradeMessage())
 *
 * // Or create a custom one
 * const customMessage = createUpgradeMessage(3, 5) // 3 out of 5 users
 * addTenantMessage(customMessage)
 * ```
 */
const mockUpgradeMessage: ExtendedChatMessage = {
  id: uuidv4(),
  role: 'assistant',
  content:
    "Hey! It appears you've reached your limit of 5 team members. Upgrade your plan to add more users and collaborate with your entire team.",
  date: new Date().toISOString(),
  type: 'upgrade',
  metadata: {
    currentUsers: 5,
    maxUsers: 5,
  },
}

const mockAreaLimitMessage: ExtendedChatMessage = {
  id: 'welcome-area-limit',
  role: 'assistant',
  content:
    "Hey! It appears you've reached the maximum project area for your current plan. Upgrade to continue managing your projects without limits.",
  date: new Date().toISOString(),
  type: 'area-limit',
}

const mockWhiteLabelMessage: ExtendedChatMessage = {
  id: 'welcome-white-label',
  role: 'assistant',
  content:
    'Great! Let’s make Atlas your own with complete with labeling capabilities, by presenting a fully branded experience to your clients and team.',
  date: new Date().toISOString(),
  type: 'white-label',
}

const mockExportLayersMessage: ExtendedChatMessage = {
  id: uuidv4(),
  role: 'assistant',
  content: 'I can help you export your geospatial data! Your project contains the following layers:',
  date: new Date().toISOString(),
  type: 'export-layers',
}

const mockCreateProjectMessage: ExtendedChatMessage = {
  id: uuidv4(),
  role: 'assistant',
  content:
    'Welcome to NatureOS! Glad you stopped by. Create a new project by filling in the fields below.',
  date: new Date().toISOString(),
  type: 'create-project',
}

const mockMiomboReportMessage: ExtendedChatMessage = {
  id: 'miombo-report',
  role: 'assistant',
  content:
    "Hello! I'm Copa, your AI assistant for carbon project insights.\n\nThis project is located in the Miombo woodland ecoregion - one of the most important carbon sequestration areas in Africa. The Miombo woodlands store approximately 14 billion tonnes of carbon and present substantial opportunities for REDD+ and afforestation initiatives.\n\nI've prepared a detailed Miombo Woodland Report for your region. You can download it below, or ask me specific questions about carbon projects in this area.",
  date: new Date().toISOString(),
  type: 'miombo-report',
}

/**
 * Helper function to create an upgrade message with custom user limits
 */
export const createUpgradeMessage = (currentUsers: number, maxUsers: number): ExtendedChatMessage => ({
  id: uuidv4(),
  role: 'assistant',
  content: `Hey! It appears you've reached your limit of ${maxUsers} team members. Upgrade your plan to add more users and collaborate with your entire team.`,
  date: new Date().toISOString(),
  type: 'upgrade',
  metadata: {
    currentUsers,
    maxUsers,
  },
})

export const getUpgradeMessageByType = (
  type: MessageType,
  metadata?: Partial<ExtendedChatMessage['metadata']>,
): ExtendedChatMessage | null => {
  switch (type) {
    case 'upgrade': {
      if (metadata?.currentUsers && metadata?.maxUsers) {
        return createUpgradeMessage(metadata.currentUsers, metadata.maxUsers)
      }
      return mockUpgradeMessage
    }
    case 'area-limit':
      return mockAreaLimitMessage
    case 'white-label':
      return mockWhiteLabelMessage
    case 'export-layers':
      return mockExportLayersMessage
    case 'create-project':
      return mockCreateProjectMessage
    case 'miombo-report':
      return mockMiomboReportMessage
    case 'text':
    default:
      return null
  }
}
